import {
  Element,
  ElementState,
  ElementType,
  Group,
  isRectLikeElement,
  Trapezoid,
} from '@ritabygglov/core';
import { useCallback } from 'react';
import { CommandManager } from '../../../commands/CommandManager';
import { CreateCommand } from '../../../commands/CreateCommand';
import { DeleteCommand } from '../../../commands/DeleteCommand';
import { UpdateCommand } from '../../../commands/UpdateCommand';
import CanvasSession from '../../../services/CanvasSession';
import {
  cloneElementsWithNewIds,
  expandGroups,
  resolveGroupTransformTarget,
  rotateElementAroundPivot,
  scaleChildInRotatedGroup,
  topLevelIds,
} from '../../../helpers/groupHelpers';
import {
  applyResizeNormalization,
  computeMultiselectBBox,
  constrainCornerToAspectRatio,
  copy,
  resizeRect,
  rotateCenter,
  getEdgeDragGeometry,
} from '../../../helpers/utility';
import {
  AppState,
  ClientCoordinates,
  Coordinate,
  Corner,
  Edge,
  isCorner,
  isEdge,
  LineSegment,
  SelectionCoordinates,
  SelectionMode,
  SelectionModes,
  ViewBox,
} from '../../../types';
import { elementHandlerRegistry } from '../handlers/elementHandlers';
import {
  measureTextContentHeight,
  setupMovingElement,
  setupResizeElement,
  setupRotateElement,
  updateElementCoords,
} from '../helpers';
import { ResizeContext, RotateContext } from '../types/interactionTypes';
import { useSnapping } from './useSnapping';

const MULTISELECT_PREFIX = 'multiselect';

const parseMultiselectResizeAnchor = (id: string): Corner | Edge => {
  if (id.endsWith('top-left')) return Corner.TopLeft;
  if (id.endsWith('top-right')) return Corner.TopRight;
  if (id.endsWith('bottom-right')) return Corner.BottomRight;
  if (id.endsWith('bottom-left')) return Corner.BottomLeft;
  if (id.endsWith('top')) return Edge.Top;
  if (id.endsWith('bottom')) return Edge.Bottom;
  if (id.endsWith('left')) return Edge.Left;
  if (id.endsWith('right')) return Edge.Right;
  throw new Error(`Cannot parse multiselect resize anchor from id: ${id}`);
};

const scaleElementInBbox = (
  origEl: Element,
  origBboxX: number,
  origBboxY: number,
  scaleX: number,
  scaleY: number,
  newBboxX: number,
  newBboxY: number
): Element => {
  if (origEl.type === ElementType.Polyline) {
    return {
      ...origEl,
      points: origEl.points.map((p) => ({
        ...p,
        x: newBboxX + (p.x - origBboxX) * scaleX,
        y: newBboxY + (p.y - origBboxY) * scaleY,
      })),
    };
  }
  const scaled: Element = {
    ...origEl,
    x: newBboxX + (origEl.x - origBboxX) * scaleX,
    y: newBboxY + (origEl.y - origBboxY) * scaleY,
    width: origEl.width * scaleX,
    height: origEl.height * scaleY,
  };
  if (origEl.type === ElementType.Trapezoid) {
    return {
      ...(scaled as Trapezoid),
      topWidth: (origEl as Trapezoid).topWidth * scaleX,
      bottomWidth: (origEl as Trapezoid).bottomWidth * scaleX,
    };
  }
  return scaled;
};

const calculateDuplicationOffset = (
  elements: Element[],
  cursorX?: number,
  cursorY?: number
): { offsetX: number; offsetY: number } => {
  if (cursorX === undefined || cursorY === undefined) {
    return { offsetX: 50, offsetY: 50 };
  }

  const coordinates = elements.flatMap((element) => {
    if (element.type === ElementType.Polyline) {
      return element.points.map((p) => ({ x: p.x, y: p.y }));
    } else {
      return [{ x: element.x, y: element.y }];
    }
  });

  const minX = Math.min(...coordinates.map((c) => c.x));
  const minY = Math.min(...coordinates.map((c) => c.y));

  return {
    offsetX: cursorX - minX,
    offsetY: cursorY - minY,
  };
};

export interface ElementTransformationDeps {
  appState: AppState;
  session: CanvasSession;
  commandManager: CommandManager;
  selectedElements: string[];
  setSelectedElements: React.Dispatch<React.SetStateAction<string[]>>;
  selectionCoordinates: SelectionCoordinates;
  setSelectionCoordinates: React.Dispatch<
    React.SetStateAction<SelectionCoordinates>
  >;
  selectionMode: SelectionMode;
  setSelectionMode: React.Dispatch<React.SetStateAction<SelectionMode>>;
  viewBox: ViewBox;
  isShiftPressed: boolean;
  isMetaPressed: boolean;
  setSnapLines: React.Dispatch<React.SetStateAction<LineSegment[]>>;
  setSnapPoints: React.Dispatch<React.SetStateAction<Coordinate[]>>;
}

export const useElementTransformation = (hooks: ElementTransformationDeps) => {
  const {
    appState,
    session,
    commandManager,
    selectedElements,
    setSelectedElements,
    selectionCoordinates,
    setSelectionCoordinates,
    selectionMode,
    setSelectionMode,
    viewBox,
    isShiftPressed,
    isMetaPressed,
    setSnapLines,
    setSnapPoints,
  } = hooks;

  const {
    snapToElements,
    snapEdgeToElements,
    matchesAtPoint,
    applySnapping,
    clearSnapping,
  } = useSnapping(
    setSnapLines,
    setSnapPoints,
    isShiftPressed,
    isMetaPressed,
    viewBox.scale
  );

  const onStartResizing = useCallback(
    (
      clientCoordinates: ClientCoordinates,
      e: React.MouseEvent<SVGSVGElement, MouseEvent>,
      id: string
    ) => {
      const { group, children } = resolveGroupTransformTarget(
        selectedElements,
        appState.elements
      );
      const isMultiTarget = group !== undefined || selectedElements.length > 1;

      if (id.startsWith(`${MULTISELECT_PREFIX}-resize-`) && isMultiTarget) {
        const anchor = parseMultiselectResizeAnchor(id);
        // A persisted group's own x/y/width/height IS its local (possibly
        // rotated) frame — use it directly rather than recomputing an
        // axis-aligned union from children, so a rotated group resizes
        // along its own tilted edges instead of world axes.
        const bbox = group
          ? { x: group.x, y: group.y, width: group.width, height: group.height }
          : computeMultiselectBBox(children);
        if (!bbox) return;
        setSelectionCoordinates({
          ...selectionCoordinates,
          initialX: bbox.x,
          initialY: bbox.y,
          initialWidth: bbox.width,
          initialHeight: bbox.height,
          selectedAnchor: anchor,
          originElements: group ? [group, ...children] : children,
        });
        setSelectionMode({ ...selectionMode, type: SelectionModes.Resizing });
        return;
      }

      const elementId = id.split('-resize-')[0];
      const element = appState.elements[elementId];
      setSelectedElements([elementId]);
      setupResizeElement(
        e,
        element,
        setSelectionCoordinates,
        selectionCoordinates,
        setSelectionMode,
        selectionMode,
        clientCoordinates
      );
    },
    [
      selectedElements,
      appState.elements,
      setSelectedElements,
      setSelectionCoordinates,
      selectionCoordinates,
      setSelectionMode,
      selectionMode,
    ]
  );

  const onResizingElement = useCallback(
    (clientCoordinates: ClientCoordinates, isEphemeral = true) => {
      const {
        selectedAnchor,
        originElements,
        initialX,
        initialY,
        initialWidth,
        initialHeight,
      } = selectionCoordinates;

      if (selectedAnchor === null) return;

      const groupIndex = originElements.findIndex(
        (el) => el.type === ElementType.Group
      );
      const groupOriginElement =
        groupIndex >= 0 ? (originElements[groupIndex] as Group) : undefined;

      if (
        groupOriginElement &&
        initialX !== null &&
        initialY !== null &&
        initialWidth !== null &&
        initialHeight !== null
      ) {
        if (typeof selectedAnchor === 'number') return;

        const mouse =
          isShiftPressed && isCorner(selectedAnchor)
            ? constrainCornerToAspectRatio(
                selectedAnchor,
                clientCoordinates.x,
                clientCoordinates.y,
                {
                  x: initialX,
                  y: initialY,
                  width: initialWidth,
                  height: initialHeight,
                  rotate: groupOriginElement.rotate,
                }
              )
            : clientCoordinates;

        // Rotation-aware: resize the group's own local (possibly rotated)
        // frame via the same math a single rotated Rect already uses, then
        // carry each child through that same local-frame scale (de-rotate,
        // scale, re-rotate) so the whole group resizes along its own tilted
        // edges instead of world axes.
        const [rawWidth, rawHeight, rawX, rawY] = resizeRect(
          selectedAnchor,
          mouse.x,
          mouse.y,
          {
            x: initialX,
            y: initialY,
            width: initialWidth,
            height: initialHeight,
            rotate: groupOriginElement.rotate,
          }
        );

        const origW = Math.max(1, Math.abs(initialWidth));
        const origH = Math.max(1, Math.abs(initialHeight));
        const newWidth = Math.abs(rawWidth);
        const newHeight = Math.abs(rawHeight);
        const newLocalX = rawWidth < 0 ? rawX + rawWidth : rawX;
        const newLocalY = rawHeight < 0 ? rawY + rawHeight : rawY;
        const scaleX = newWidth / origW;
        const scaleY = newHeight / origH;

        const origCenter = {
          cx: initialX + initialWidth / 2,
          cy: initialY + initialHeight / 2,
        };

        // rawX/rawY above are expressed in resizeRect's own frame, which
        // solves for a NEW center from the world-space anchor + dragged
        // corner (see resizeRotatedRectangle) — that's the right frame for
        // the group's own stored box (it's what makes the anchor corner
        // stay put when rendered around its own new center), but it's a
        // *different* pivot than origCenter, so it can't also be used as
        // the children's local-frame origin (see scaleChildInRotatedGroup).
        // Re-derive that origin by de-rotating the mouse around origCenter
        // and resizing in that already-unrotated view instead — width/height
        // come out identical (rotation only changes a vector difference by
        // a pivot-independent amount) but x/y land in the frame children
        // need.
        const [localMouseX, localMouseY] = rotateCenter(
          mouse.x,
          mouse.y,
          origCenter.cx,
          origCenter.cy,
          -groupOriginElement.rotate
        );
        const [rawWidthLocal, rawHeightLocal, rawXLocal, rawYLocal] =
          resizeRect(selectedAnchor, localMouseX, localMouseY, {
            x: initialX,
            y: initialY,
            width: initialWidth,
            height: initialHeight,
            rotate: 0,
          });
        const newLocalXInGroupFrame =
          rawWidthLocal < 0 ? rawXLocal + rawWidthLocal : rawXLocal;
        const newLocalYInGroupFrame =
          rawHeightLocal < 0 ? rawYLocal + rawHeightLocal : rawYLocal;

        const children = originElements.filter((_, i) => i !== groupIndex);
        const scaledChildren = children.map((origEl) => {
          const scaled = scaleChildInRotatedGroup(
            origEl,
            {
              x: initialX,
              y: initialY,
              rotate: groupOriginElement.rotate,
              ...origCenter,
            },
            scaleX,
            scaleY,
            newLocalXInGroupFrame,
            newLocalYInGroupFrame
          );
          const currentEl = appState.elements[origEl.id];
          return currentEl
            ? { ...scaled, userVersion: currentEl.userVersion }
            : scaled;
        });

        const currentGroupEl = appState.elements[groupOriginElement.id];
        const updatedGroup: Group = {
          ...groupOriginElement,
          x: newLocalX,
          y: newLocalY,
          width: newWidth,
          height: newHeight,
          userVersion:
            currentGroupEl && currentGroupEl.type === ElementType.Group
              ? currentGroupEl.userVersion
              : groupOriginElement.userVersion,
        };

        const command = new UpdateCommand(
          [updatedGroup, ...scaledChildren],
          originElements,
          session.currentUserId,
          isEphemeral
        );
        commandManager.executeCommand(command);
        return;
      }

      if (
        originElements.length > 1 &&
        initialX !== null &&
        initialY !== null &&
        initialWidth !== null &&
        initialHeight !== null
      ) {
        if (typeof selectedAnchor === 'number') return;

        const bboxRight = initialX + initialWidth;
        const bboxBottom = initialY + initialHeight;
        const { x: cx, y: cy } =
          isShiftPressed && isCorner(selectedAnchor)
            ? constrainCornerToAspectRatio(
                selectedAnchor,
                clientCoordinates.x,
                clientCoordinates.y,
                {
                  x: initialX,
                  y: initialY,
                  width: initialWidth,
                  height: initialHeight,
                  rotate: 0,
                }
              )
            : clientCoordinates;

        let newX: number, newY: number, newWidth: number, newHeight: number;
        switch (selectedAnchor) {
          case Corner.TopLeft:
            newX = cx;
            newY = cy;
            newWidth = bboxRight - cx;
            newHeight = bboxBottom - cy;
            break;
          case Corner.TopRight:
            newX = initialX;
            newY = cy;
            newWidth = cx - initialX;
            newHeight = bboxBottom - cy;
            break;
          case Corner.BottomRight:
            newX = initialX;
            newY = initialY;
            newWidth = cx - initialX;
            newHeight = cy - initialY;
            break;
          case Corner.BottomLeft:
            newX = cx;
            newY = initialY;
            newWidth = bboxRight - cx;
            newHeight = cy - initialY;
            break;
          case Edge.Top:
            newX = initialX;
            newY = cy;
            newWidth = initialWidth;
            newHeight = bboxBottom - cy;
            break;
          case Edge.Bottom:
            newX = initialX;
            newY = initialY;
            newWidth = initialWidth;
            newHeight = cy - initialY;
            break;
          case Edge.Left:
            newX = cx;
            newY = initialY;
            newWidth = bboxRight - cx;
            newHeight = initialHeight;
            break;
          case Edge.Right:
            newX = initialX;
            newY = initialY;
            newWidth = cx - initialX;
            newHeight = initialHeight;
            break;
          default:
            return;
        }

        const origW = Math.max(1, Math.abs(initialWidth));
        const origH = Math.max(1, Math.abs(initialHeight));
        const scaleX = Math.abs(newWidth) / origW;
        const scaleY = Math.abs(newHeight) / origH;
        const resolvedNewX = newWidth < 0 ? newX + newWidth : newX;
        const resolvedNewY = newHeight < 0 ? newY + newHeight : newY;

        const updatedElements = originElements.map((origEl) => {
          const scaled = scaleElementInBbox(
            origEl,
            initialX,
            initialY,
            scaleX,
            scaleY,
            resolvedNewX,
            resolvedNewY
          );
          // Use the current version from appState so each ephemeral update
          // passes the CRDT isNewerVersion check (originElements has stale version).
          const currentEl = appState.elements[origEl.id];
          return currentEl
            ? { ...scaled, userVersion: currentEl.userVersion }
            : scaled;
        });

        const command = new UpdateCommand(
          updatedElements,
          originElements,
          session.currentUserId,
          isEphemeral
        );
        commandManager.executeCommand(command);
        return;
      }

      const selectedElement = selectedElements[0];
      if (!selectedElement || selectedAnchor === null) return;

      const element = appState.elements[selectedElement];
      if (!element) return;
      let { x, y } = { x: clientCoordinates.x, y: clientCoordinates.y };

      const isEdgeDrag = isEdge(selectedAnchor);
      const keepAspectRatio =
        isShiftPressed &&
        isCorner(selectedAnchor) &&
        isRectLikeElement(element) &&
        element.type !== ElementType.Text;
      let snap: ReturnType<typeof snapToElements> | undefined;
      if (keepAspectRatio) {
        ({ x, y } = constrainCornerToAspectRatio(
          selectedAnchor,
          clientCoordinates.x,
          clientCoordinates.y,
          element
        ));
        clearSnapping();
      } else if (!isEdgeDrag) {
        snap = snapToElements({
          x: clientCoordinates.x,
          y: clientCoordinates.y,
          elements: appState.elements,
          element,
          excludeSelf: true,
        });

        x = snap.dx;
        y = snap.dy;
      } else {
        const edgeGeometry = getEdgeDragGeometry(element, selectedAnchor);
        if (edgeGeometry) {
          const { edgePoints, freeAxis, edgeAnchor } = edgeGeometry;
          const dragDistance =
            (clientCoordinates.x - edgeAnchor.x) * freeAxis.x +
            (clientCoordinates.y - edgeAnchor.y) * freeAxis.y;

          snap = snapEdgeToElements({
            deltaX: dragDistance * freeAxis.x,
            deltaY: dragDistance * freeAxis.y,
            freeAxis,
            edgePoints,
            elements: appState.elements,
            elementId: element.id,
          });

          x = edgeAnchor.x + snap.dx;
          y = edgeAnchor.y + snap.dy;
        } else {
          clearSnapping();
        }
      }

      const resizeContext: ResizeContext = {
        element,
        coordinates: { x, y },
        selectedAnchor,
        selectionMode,
        selectionCoordinates,
        viewBox,
        userId: session.currentUserId,
        isEphemeral,
        isShiftPressed,
        isMetaPressed,
        elements: appState.elements,
        minHeight:
          element.type === ElementType.Text
            ? measureTextContentHeight(element.id)
            : undefined,
      };

      const elementUpdate = elementHandlerRegistry.handleResize(
        element.type,
        resizeContext
      );

      if (!elementUpdate) {
        throw new Error(
          `No element update returned from handleResize for element type ${element.type}`
        );
      }

      const { normalizedUpdate: finalUpdate, newAnchor } =
        applyResizeNormalization(elementUpdate, selectedAnchor);

      if (newAnchor !== null) {
        setSelectionCoordinates({
          ...selectionCoordinates,
          selectedAnchor: newAnchor,
        });
      }

      // if shift is pressed -> recalculate displayed snaplines based on corrected point
      if (
        element.type === ElementType.Polyline &&
        finalUpdate.type === ElementType.Polyline &&
        isShiftPressed &&
        typeof selectedAnchor === 'number'
      ) {
        const corrected = finalUpdate.points[selectedAnchor];
        const rematched = matchesAtPoint(
          corrected,
          appState.elements,
          element.id
        );
        applySnapping({
          dx: corrected.x,
          dy: corrected.y,
          matchedLines: rematched.matchedLines,
          matchedPoints: rematched.matchedPoints,
        });
      } else if (snap) {
        applySnapping(snap);
      }

      const command = new UpdateCommand(
        [finalUpdate],
        [element],
        session.currentUserId,
        isEphemeral
      );
      commandManager.executeCommand(command);
    },
    [
      selectedElements,
      selectionCoordinates,
      appState.elements,
      matchesAtPoint,
      selectionMode,
      viewBox,
      session.currentUserId,
      isShiftPressed,
      isMetaPressed,
      commandManager,
      snapToElements,
      snapEdgeToElements,
      applySnapping,
      clearSnapping,
      setSelectionCoordinates,
    ]
  );

  const onStartRotating = useCallback(
    (e: React.MouseEvent<SVGSVGElement, MouseEvent>, id: string) => {
      const { group, children } = resolveGroupTransformTarget(
        selectedElements,
        appState.elements
      );
      const isMultiTarget = group !== undefined || selectedElements.length > 1;

      if (id === `${MULTISELECT_PREFIX}-rotate` && isMultiTarget) {
        // A persisted group's own center is the authoritative pivot — an
        // axis-aligned union of its (possibly already-rotated) children
        // isn't guaranteed to share that same center unless they happen to
        // be arranged symmetrically around it, so recomputing from children
        // would let the pivot drift with each rotation.
        const bbox = group
          ? { x: group.x, y: group.y, width: group.width, height: group.height }
          : computeMultiselectBBox(children);
        if (!bbox) return;
        const pivotX = bbox.x + bbox.width / 2;
        const pivotY = bbox.y + bbox.height / 2;
        const canvasX = e.clientX * viewBox.scale + viewBox.x;
        const canvasY = e.clientY * viewBox.scale + viewBox.y;
        setSelectionCoordinates({
          ...selectionCoordinates,
          startX: canvasX,
          startY: canvasY,
          xOffset: pivotX,
          yOffset: pivotY,
          originElements: group ? [group, ...children] : children,
        });
        setSelectionMode({ ...selectionMode, type: SelectionModes.Rotating });
        return;
      }

      const elementId = id.split('-rotate')[0];
      const element = appState.elements[elementId];
      setSelectedElements([elementId]);
      setupRotateElement(
        e,
        element,
        setSelectionCoordinates,
        selectionCoordinates,
        setSelectionMode,
        selectionMode,
        viewBox.scale
      );
    },
    [
      selectedElements,
      appState.elements,
      viewBox,
      setSelectedElements,
      setSelectionCoordinates,
      selectionCoordinates,
      setSelectionMode,
      selectionMode,
    ]
  );

  const onRotating = useCallback(
    (clientCoordinates: ClientCoordinates, isEphemeral = true) => {
      const { originElements, startX, startY, xOffset, yOffset } =
        selectionCoordinates;

      if (originElements.length > 1 && startX !== null && startY !== null) {
        const pivotX = xOffset;
        const pivotY = yOffset;
        const initialAngle =
          (Math.atan2(startY - pivotY, startX - pivotX) * 180) / Math.PI;
        const currentAngle =
          (Math.atan2(
            clientCoordinates.y - pivotY,
            clientCoordinates.x - pivotX
          ) *
            180) /
          Math.PI;
        const snapIncrement = 15;
        let deltaAngle = currentAngle - initialAngle;
        if (isShiftPressed) {
          deltaAngle = Math.round(deltaAngle / snapIncrement) * snapIncrement;
        }

        // A persisted Group rotates rigidly like any other element — its
        // own entry goes through the same rotateElementAroundPivot as its
        // children, picking up a real `rotate` value and a shifted center.
        // (Its x/y/width/height keep describing the group's own local,
        // unrotated frame — exactly like a single Rect.)
        const updatedElements = originElements.map((origEl) => {
          const rotated = rotateElementAroundPivot(
            origEl,
            pivotX,
            pivotY,
            deltaAngle
          );
          const currentEl = appState.elements[origEl.id];
          return currentEl
            ? { ...rotated, userVersion: currentEl.userVersion }
            : rotated;
        });

        const command = new UpdateCommand(
          updatedElements,
          originElements,
          session.currentUserId,
          isEphemeral
        );
        commandManager.executeCommand(command);
        return;
      }

      const selectedElement = selectedElements[0];
      if (!selectedElement) return;

      const element = copy(appState.elements[selectedElement]);

      const rotateContext: RotateContext = {
        element,
        coordinates: clientCoordinates,
        selectionMode,
        selectionCoordinates,
        viewBox,
        userId: session.currentUserId,
        isEphemeral,
        isShiftPressed,
      };

      const elementUpdate = elementHandlerRegistry.handleRotate(
        element.type,
        rotateContext
      );

      if (!elementUpdate) {
        throw new Error(
          `No element update returned from handleRotate for element type ${element.type}`
        );
      }

      const command = new UpdateCommand(
        [elementUpdate],
        [element],
        session.currentUserId,
        isEphemeral
      );
      commandManager.executeCommand(command);
    },
    [
      selectedElements,
      appState.elements,
      selectionMode,
      selectionCoordinates,
      viewBox,
      session.currentUserId,
      isShiftPressed,
      commandManager,
    ]
  );

  const onSelected = useCallback(
    (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      const { startX, startY, originElements } = selectionCoordinates;
      if (startX === null || startY === null) return;

      e.preventDefault();

      const diffX = e.clientX * viewBox.scale - startX;
      const diffY = e.clientY * viewBox.scale - startY;
      let dx = diffX;
      let dy = diffY;

      // A persisted Group snaps as a single rigid unit, using its own bbox
      // as the reference element — its own children (already included in
      // originElements alongside it) must be excluded as candidates so the
      // group doesn't "snap" to its own (stale, pre-drag) contents.
      const groupOriginElement = originElements.find(
        (el) => el.type === ElementType.Group
      );
      const elementsForGroupSnap = groupOriginElement
        ? Object.fromEntries(
            Object.entries(appState.elements).filter(
              ([id]) => !originElements.some((el) => el.id === id)
            )
          )
        : undefined;

      // TODO: This should be moved to the MultiUpdateCommand
      // Driven by originElements (not selectedElements) so a group's
      // children move along with it even though only the group id is
      // "selected" — originElements is expanded to [group, ...children] at
      // gesture start (see setupMoving) for that case.
      const elementChanges = originElements
        .map((originElement) => {
          const element = appState.elements[originElement.id];
          if (!element) return;

          if (isShiftPressed) {
            //todo: when shift if pressed, we should only snap in the intended major direction
            if (Math.abs(diffX) >= Math.abs(diffY)) {
              dy = 0;
            } else {
              dx = 0;
            }
          }

          if (!isMetaPressed) {
            // For a group move, only snap once (on the group's own
            // iteration, which setupMoving always orders first) — children
            // then just inherit the already-snapped dx/dy.
            const isGroupChildIteration =
              groupOriginElement !== undefined &&
              originElement !== groupOriginElement;
            const shouldSnap =
              (originElements.length === 1 ||
                groupOriginElement !== undefined) &&
              !isGroupChildIteration;

            if (shouldSnap) {
              const snap = snapToElements({
                x: dx,
                y: dy,
                element: groupOriginElement ?? originElement,
                elements: elementsForGroupSnap ?? appState.elements,
              });
              applySnapping(snap);
              dx = snap.dx;
              dy = snap.dy;
            }
          } else if (isMetaPressed) {
            clearSnapping();
          }
          return updateElementCoords(element, dx, dy, originElement);
        })
        .filter((e) => e !== undefined);

      const command = new UpdateCommand(
        elementChanges,
        [],
        session.currentUserId,
        true
      );
      commandManager.executeCommand(command);
    },
    [
      selectionCoordinates,
      viewBox.scale,
      session.currentUserId,
      commandManager,
      isShiftPressed,
      appState.elements,
      isMetaPressed,
      snapToElements,
      applySnapping,
      clearSnapping,
    ]
  );

  const setupMoving = useCallback(
    (e: React.MouseEvent<SVGSVGElement, MouseEvent>, elements: Element[]) => {
      // Expand every selected Group into itself + its children — not just
      // when it's the sole selection — so a Group moves along with its
      // children even when selected alongside other elements. Previously
      // only the "sole selected element is a Group" case was expanded,
      // which left a group's children behind (only its own bbox fields
      // moved) whenever it was part of a mixed selection.
      const expanded = expandGroups(elements, appState.elements);
      setupMovingElement(
        e,
        expanded,
        setSelectionCoordinates,
        selectionCoordinates,
        viewBox.scale
      );
    },
    [
      setSelectionCoordinates,
      selectionCoordinates,
      viewBox.scale,
      appState.elements,
    ]
  );

  const onStopPolyline = useCallback(
    (shouldRemoveLastPoint = true) => {
      // TODO: All of thse polyline handling should be moved to the PolylineCommand
      const currentElement = copy(appState.elements[selectedElements[0]]);
      const element = copy(appState.elements[selectedElements[0]]) as Element;
      if (!element || element.type !== ElementType.Polyline) return;
      const isEphemeral = false;
      if (shouldRemoveLastPoint) {
        element.points = element.points.slice(0, -1);
      }
      const shouldRemoveItem = element.points.length === 1;
      element.state = ElementState.Visible;
      if (shouldRemoveItem) {
        const command = new DeleteCommand([element], session?.currentUserId);
        commandManager.executeCommand(command);
      } else {
        const command = new UpdateCommand(
          [element],
          [currentElement],
          session?.currentUserId,
          isEphemeral
        );
        commandManager.executeCommand(command);
      }

      // With simplified commands, no need to complete operation

      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.None,
        elementType: undefined,
        lineType: undefined,
      });
    },
    [
      appState.elements,
      commandManager,
      session,
      selectedElements,
      selectionMode,
      setSelectionMode,
    ]
  );
  const deleteElement = useCallback(() => {
    const elements = selectedElements
      .map((id) => appState.elements[id])
      .filter((el): el is Element => el !== undefined);
    if (elements.length === 0) return;
    const command = new DeleteCommand(elements, session?.currentUserId);
    commandManager.executeCommand(command);

    setSelectedElements([]);
    setSelectionMode({
      ...selectionMode,
      type: SelectionModes.None,
      elementType: undefined,
      lineType: undefined,
    });
  }, [
    selectedElements,
    session?.currentUserId,
    commandManager,
    setSelectedElements,
    setSelectionMode,
    selectionMode,
    appState.elements,
  ]);

  const duplicateElement = useCallback(
    (cursorX?: number, cursorY?: number) => {
      // Expand groups first so a duplicated Group brings its children, then
      // clone the whole set together — cloneElementsWithNewIds re-points the
      // copied group at the copied children rather than the originals.
      const selected = selectedElements
        .map((id) => appState.elements[id])
        .filter((el): el is Element => el !== undefined);
      if (selected.length === 0) return;

      const elements = expandGroups(selected, appState.elements);
      const { offsetX, offsetY } = calculateDuplicationOffset(
        elements,
        cursorX,
        cursorY
      );
      const { elements: newElements, idMap } = cloneElementsWithNewIds(
        elements,
        offsetX,
        offsetY,
        Object.keys(appState.elements).length + 1
      );
      if (newElements.length === 0) return;

      const command = new CreateCommand(newElements, session?.currentUserId);
      commandManager.executeCommand(command);

      setSelectedElements(
        selectedElements
          .map((id) => idMap.get(id))
          .filter((id): id is string => id !== undefined)
      );
    },
    [
      selectedElements,
      session?.currentUserId,
      commandManager,
      setSelectedElements,
      appState.elements,
    ]
  );

  const onStartTextEditing = useCallback(
    (element: Element) => {
      if (element.type !== ElementType.Text) return;
      setSelectionMode({
        ...selectionMode,
        type: SelectionModes.TextEditing,
        elementType: ElementType.Text,
      });
    },
    [selectionMode, setSelectionMode]
  );

  const pasteElements = useCallback(
    (clipboardText: string, cursorX: number, cursorY: number) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(clipboardText);
      } catch {
        return;
      }
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        !('_ritabygglov' in parsed) ||
        !Array.isArray((parsed as Record<string, unknown>).elements)
      ) {
        return;
      }
      const elements = (
        parsed as { _ritabygglov: boolean; elements: Element[] }
      ).elements;
      if (elements.length === 0) return;

      const { offsetX, offsetY } = calculateDuplicationOffset(
        elements,
        cursorX,
        cursorY
      );

      const { elements: newElements, idMap } = cloneElementsWithNewIds(
        elements,
        offsetX,
        offsetY,
        Object.keys(appState.elements).length + 1
      );
      if (newElements.length === 0) return;

      const command = new CreateCommand(newElements, session?.currentUserId);
      commandManager.executeCommand(command);
      // Select the pasted group(s) / loose elements, not the children the
      // clipboard carried along to make the group copy self-contained.
      setSelectedElements(
        topLevelIds(elements)
          .map((id) => idMap.get(id))
          .filter((id): id is string => id !== undefined)
      );
    },
    [
      appState.elements,
      commandManager,
      session?.currentUserId,
      setSelectedElements,
    ]
  );

  const onStopSelection = useCallback(() => {
    // With simplified commands, transformations are applied immediately
    // No need to complete operations
    // TODO: Since we have the originElements, we can in the final update include the
    // the full element and the originalElement, so we can undo this one properly.

    const isEphemeral = false;
    const originElements = selectionCoordinates.originElements;
    // Driven by originElements (not selectedElements) so a group's children
    // are included in the final commit — see onSelected/setupMoving.
    const updatedElements = originElements
      .map((el) => appState.elements[el.id])
      .filter((el): el is Element => el !== undefined);

    const command = new UpdateCommand(
      updatedElements,
      originElements,
      session.currentUserId,
      isEphemeral
    );
    commandManager.executeCommand(command);
  }, [
    appState.elements,
    commandManager,
    session.currentUserId,
    selectionCoordinates.originElements,
  ]);

  return {
    onStartResizing,
    onResizingElement,
    onStartRotating,
    onRotating,
    onSelected,
    onStartTextEditing,
    onStopSelection,
    onStopPolyline,
    setupMoving,
    clearSnapping,
    deleteElement,
    duplicateElement,
    pasteElements,
  };
};
